import { createContext, useCallback, useContext, useRef, useState } from 'react';
import BsToastContainer from 'react-bootstrap/ToastContainer';
import BsToast from 'react-bootstrap/Toast';
import { IconCheckCircle, IconAlertTriangle, IconInfo } from '../atoms/Icon';

const ToastContext = createContext(null);

const VARIANTS = {
  success: { Icon: IconCheckCircle, color: '#2a9d5c', bg: '#e6f8ef' },
  danger: { Icon: IconAlertTriangle, color: '#e74c3c', bg: '#fdecea' },
  info: { Icon: IconInfo, color: 'var(--color-purple-primary)', bg: 'var(--color-purple-ghost)' },
};

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);
  const idRef = useRef(0);

  const remove = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const notify = useCallback((message, variant, action) => {
    const id = ++idRef.current;
    setToasts((prev) => [...prev, { id, message, variant, action }]);
  }, []);

  const toast = {
    success: (message, opts) => notify(message, 'success', opts?.action),
    error: (message, opts) => notify(message, 'danger', opts?.action),
    info: (message, opts) => notify(message, 'info', opts?.action),
  };

  return (
    <ToastContext.Provider value={toast}>
      {children}

      <BsToastContainer position="bottom-end" className="p-3" style={{ position: 'fixed', zIndex: 2000 }}>
        {toasts.map((t) => {
          const cfg = VARIANTS[t.variant] || VARIANTS.info;
          const Icon = cfg.Icon;
          return (
            <BsToast
              key={t.id}
              show
              onClose={() => remove(t.id)}
              delay={t.action ? 6000 : 3500}
              autohide
              className="app-toast"
            >
              <div className="d-flex align-items-center gap-2 p-3">
                <div
                  style={{
                    width: 32,
                    height: 32,
                    borderRadius: '50%',
                    backgroundColor: cfg.bg,
                    color: cfg.color,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  <Icon size={16} />
                </div>
                <div style={{ fontSize: 13, color: 'var(--color-neutral)', flexGrow: 1 }}>
                  {t.message}
                </div>
                {t.action && (
                  <button
                    type="button"
                    onClick={() => { t.action.onClick(); remove(t.id); }}
                    style={{
                      border: 'none', background: 'none', flexShrink: 0,
                      color: 'var(--color-purple-primary)', fontSize: 13, fontWeight: 700, cursor: 'pointer',
                    }}
                  >
                    {t.action.label}
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => remove(t.id)}
                  aria-label="Fechar"
                  style={{
                    border: 'none', background: 'none', color: '#ccc',
                    fontSize: 18, lineHeight: 1, cursor: 'pointer', flexShrink: 0,
                  }}
                >
                  ×
                </button>
              </div>
            </BsToast>
          );
        })}
      </BsToastContainer>
    </ToastContext.Provider>
  );
}

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used within a ToastProvider');
  return ctx;
}
